import React, { useContext, useEffect, useState } from "react";
import {
    TextField, IconButton, Table, TableBody, TableCell, TableHead, TableRow,
    CircularProgress
} from "@mui/material";
import { makeStyles } from 'tss-react/mui';
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";
import { StandardTableContainer, useStandardTableStyles } from "../StandardTable";

const useStyles = makeStyles()((theme) => ({
    root: {
        width: "100%",
    },
    compartido: {
        fontSize: 11,
        color: theme.palette.text.disabled,
        display: "flex",
        alignItems: "center",
        gap: 4,
    },
    agregarFila: {
        display: "flex",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    loadingBox: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(2),
    },
}));

const ContactStatusManager = () => {
    const { classes } = useStyles();
    const tableClasses = useStandardTableStyles();
    const { user } = useContext(AuthContext);
    const companyId = user?.companyId;

    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nuevoNombre, setNuevoNombre] = useState("");
    const [guardandoNuevo, setGuardandoNuevo] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [editTexto, setEditTexto] = useState("");
    const [borrandoId, setBorrandoId] = useState(null);

    const cargar = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/contact/status/list");
            setStatuses(data.data ?? []);
        } catch (err) {
            toastError(err);
        }
        setLoading(false);
    };

    useEffect(() => {
        cargar();
    }, []);

    const handleAgregar = async () => {
        const nombre = nuevoNombre.trim();
        if (!nombre) return;

        setGuardandoNuevo(true);
        try {
            await api.post("/contact/status/create", { name: nombre });
            setNuevoNombre("");
            toast.success("Estado creado.");
            cargar();
        } catch (err) {
            toastError(err);
        }
        setGuardandoNuevo(false);
    };

    const handleEmpezarEdicion = (status) => {
        setEditandoId(status.id);
        setEditTexto(status.name);
    };

    const handleGuardarEdicion = async (statusId) => {
        const nombre = editTexto.trim();
        if (!nombre) return;

        try {
            await api.put("/contact/status/rename", { statusId, name: nombre });
            setEditandoId(null);
            toast.success("Estado actualizado.");
            cargar();
        } catch (err) {
            toastError(err);
        }
    };

    const handleBorrar = async (statusId) => {
        if (!window.confirm("¿Eliminar este estado? No se puede deshacer.")) return;

        setBorrandoId(statusId);
        try {
            await api.delete("/contact/status/delete", { data: { statusId } });
            toast.success("Estado eliminado.");
            cargar();
        } catch (err) {
            toastError(err);
        }
        setBorrandoId(null);
    };

    return (
        <SettingsCard
            icon={LabelOutlinedIcon}
            title="Estados de contacto"
            subtitle="Estos son los estados que vas a poder asignarle a un contacto (Cliente, Lead, Descartado...)."
        >
        <div className={classes.root}>
            <StandardTableContainer flush>
                <Table size="small" stickyHeader>
                    <TableHead className={tableClasses.head}>
                        <TableRow>
                            <TableCell>Nombre</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {statuses.map((status) => {
                            const esPropio = Number(status.companyId) === Number(companyId);
                            const editando = editandoId === status.id;

                            return (
                                <TableRow key={status.id} hover>
                                    <TableCell>
                                        {editando ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                autoFocus
                                                value={editTexto}
                                                onChange={(e) => setEditTexto(e.target.value)}
                                                onKeyDown={(e) => e.key === "Enter" && handleGuardarEdicion(status.id)}
                                            />
                                        ) : (
                                            <>
                                                {status.name}
                                                {!esPropio && (
                                                    <span className={classes.compartido}>
                                                        <LockOutlinedIcon style={{ fontSize: 12 }} />
                                                        Compartido
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {editando ? (
                                            <>
                                                <IconButton size="small" onClick={() => handleGuardarEdicion(status.id)}>
                                                    <CheckOutlinedIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => setEditandoId(null)}>
                                                    <CloseOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        ) : esPropio && (
                                            <>
                                                <IconButton size="small" onClick={() => handleEmpezarEdicion(status)}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disabled={borrandoId === status.id}
                                                    onClick={() => handleBorrar(status.id)}
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {loading && (
                    <div className={classes.loadingBox}>
                        <CircularProgress size={22} />
                    </div>
                )}
                {!loading && statuses.length === 0 && (
                    <div className={tableClasses.empty}>Todavía no hay estados cargados.</div>
                )}
            </StandardTableContainer>

            <div className={classes.agregarFila}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Nombre del nuevo estado"
                    value={nuevoNombre}
                    onChange={(e) => setNuevoNombre(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAgregar()}
                />
                <IconButton
                    color="primary"
                    disabled={guardandoNuevo || !nuevoNombre.trim()}
                    onClick={handleAgregar}
                >
                    <AddOutlinedIcon />
                </IconButton>
            </div>
        </div>
        </SettingsCard>
    );
};

export default ContactStatusManager;
